/* @flow */
import {edapTriggerPriceSummaryDisplay} from '@homeaway/react-price-summary';
import {trigger as edapTrigger} from './edapWrapper';
import type {ListingShape as MapHitListingShape} from '../components/MapHit/MapHit';
import type {ListingShape as HitListingShape} from '../components/Hit/Hit';

import type {Badge} from '../components/HitInfo/HitInfo';
import type {SearchParamsType} from '../reducers/searchParams';

export const LIST_HIT_CLICK = 'search.hit.click';
export const MAP_HIT_CLICK = 'search.map.hit.click';
export const MAP_MARKER_CLICK = 'search.map.property.click';
export const SEARCH_RESULT_SET_SELECTED = 'search_result_set.selected';

type HitEdapEventAction =
    | typeof LIST_HIT_CLICK
    | typeof MAP_HIT_CLICK
    | typeof MAP_MARKER_CLICK
    | typeof SEARCH_RESULT_SET_SELECTED;

type ListingShape = HitListingShape | MapHitListingShape;

export type HitEventDataListingShape = {
    listing: ListingShape,
    hitIndex?: number,
    page?: number,
    pageSize?: number
};

export type HitClickEventData = HitEventDataListingShape & {
    inputParams: SearchParamsType
};

export type MultiUnitPropertyDropDownShape = {
    propertyId: number
};

export type MultiUnitPropertyResultCountShape = {
    propertyId: number,
    resultCount: number
};

function parseBadges(listingBadges: ?(Badge[])) {
    let badges;
    if (listingBadges) {
        badges = listingBadges.map(({id}) => id);
    }

    return (badges || []).join(',');
}

export const handleBadgesNames = (badges: ?(Badge[])) => {
    let names;
    if (badges) {
        names = badges.map(({name}) => name);
    }
    return (names || []).join(',');
};

export const triggerHitEdapEvent = (
    eventData: HitEventDataListingShape,
    eventCategory: HitEdapEventAction,
    delayProcessing?: boolean,
    skipReportPositioning?: boolean,
    skipPriceSummaryTrigger?: boolean,
    isHighFrequency?: boolean
) => {
    const options = {delayProcessing};
    const {
        listingId,
        partnerBadges,
        rankedBadges,
        priceSummary,
        virtualTourBadge
    } = eventData.listing;
    const badges = [
        parseBadges(partnerBadges),
        parseBadges(rankedBadges),
        virtualTourBadge && 'virtualTourBadge'
    ]
        .filter(Boolean)
        .join(',');

    let data = {
        eventlistingids: listingId,
        badges
    };

    if (!skipReportPositioning && eventData.hitIndex !== null) {
        const relativePosition = eventData.hitIndex + 1;
        let absolutePosition;
        if (eventData.page && eventData.pageSize) {
            absolutePosition =
                (eventData.page - 1) * eventData.pageSize +
                eventData.hitIndex +
                1;
        } else {
            absolutePosition = relativePosition;
        }

        data = {
            ...data,
            absoluteposition: absolutePosition.toString(),
            relativeposition: relativePosition.toString()
        };
    }
    if (isHighFrequency) {
        data = {
            ...data,
            highfrequency: 'true'
        };
    }

    edapTrigger(eventCategory, data, options);

    if (priceSummary && !skipPriceSummaryTrigger) {
        edapTriggerPriceSummaryDisplay(priceSummary, {delayProcessing});
    }
};

export const triggerSearchResultSetSelectedEdapEvent = (
    eventData: HitClickEventData,
    eventCategory: HitEdapEventAction,
    delayProcessing?: boolean
) => {
    const options = {delayProcessing};
    const {
        listing: {
            listingId,
            virtualTourBadge,
            rankedBadges,
            superlativesBadges,
            partnerBadges
        },
        inputParams: {
            adultsCount,
            childrenCount,
            arrival,
            departure,
            petIncluded
        }
    } = eventData;
    const badges: string = [
        handleBadgesNames(partnerBadges),
        handleBadgesNames(superlativesBadges),
        handleBadgesNames(rankedBadges),
        virtualTourBadge && virtualTourBadge.name
    ]
        .filter(Boolean)
        .join(',');

    let data = {
        action_location: 'search',
        adult_count: adultsCount ? adultsCount.toString() : null,
        child_count: childrenCount ? childrenCount.toString() : null,
        listing_id: listingId,
        badges,
        date_end: departure,
        date_start: arrival,
        with_pets: String(petIncluded)
    };

    edapTrigger(eventCategory, data, options);
};

export const triggerListHitClickEDAPEvent = (eventData: HitClickEventData) => {
    const {listing, hitIndex, page, pageSize, inputParams} = eventData;
    triggerHitEdapEvent(
        {listing, hitIndex, page, pageSize},
        LIST_HIT_CLICK,
        true,
        false,
        false
    );
    triggerSearchResultSetSelectedEdapEvent(
        {listing, inputParams},
        SEARCH_RESULT_SET_SELECTED,
        false
    );
};

export const triggerMapHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MAP_HIT_CLICK, true, false, false);
};

export const triggerMarkerClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, MAP_MARKER_CLICK, false, false, true);
};

export const triggerExpandedGroupHitClickEDAPEvent = (
    eventData: HitEventDataListingShape
) => {
    triggerHitEdapEvent(eventData, LIST_HIT_CLICK, true, true, false);
};
